import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Select, { components, createFilter } from 'react-select';
import { FixedSizeList as List  } from 'react-window';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import cx from 'classnames';

import { DropdownBackdrop } from "./components/backdrop";

import './Dropdown.scss';

export const DROPDOWN_NO_VALUE = null;

export { components };

const DropdownIndicator = (props) => {
    return components.DropdownIndicator && (
        <components.DropdownIndicator {...props}>
            <span className="bs-caret"><span className="caret" style={{borderTop: '4px solid black'}}></span></span>
        </components.DropdownIndicator>
    );
};

class Option extends Component {
    shouldComponentUpdate(nextProps) {
        return !isEqual(nextProps.style, this.props.style) || !isEqual(nextProps.data, this.props.data);
    }

    render() {
        const { data, style } = this.props;
        const label = get(data, 'props.data.label', '');
    
        return (
            <div 
                className="Dropdown__menu__option-wrapper" 
                title={label} 
                style={style}
            >
                {data}
            </div>
        );
    }
}

const optionHeight = 28;
const menuStyle = { maxHeight: 200 };
const menuPortalStyles = (baseStyles) => ({
    ...baseStyles,
    zIndex: 9999999999,
});

class MenuList extends Component {
    renderOption = ({ index, style }) => {
        const { children } = this.props;

        return <Option data={children[index]} style={style} />;
    }

    render() {
        const { options, children, getValue } = this.props;
        const [value] = getValue();
        const initialOffset = options?.indexOf(value) * optionHeight;
    
        return (
            <List
                className="Dropdown__menu"
                height={options ? options.length * optionHeight : undefined}
                itemCount={children.length}
                itemSize={optionHeight}
                initialScrollOffset={initialOffset}
                style={menuStyle}
            >
                {this.renderOption}
            </List>
        );
    }
}

export default class Dropdown extends Component {
    constructor(props) {
        super(props);
        this.selectRef = React.createRef();
        this.getMenuPlacement = this.getMenuPlacement.bind(this);
        this.state = {menu: 'auto', styles: props.styles, menuIsOpen: this.props.menuIsOpen || false};
    }

    handleOnChange = (option) => {
        this.props.onValueChanged(option ? option.value : DROPDOWN_NO_VALUE);
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
        this.getMenuPlacement();
    }

    shouldComponentUpdate(nextProps, nextState) {
        const prepareOptionsForComparison = (options = []) => options.map((opt) => {
            if (typeof opt === 'object' && typeof opt.label === 'object') {
                const { label, ...rest } = opt;

                return {
                    ...rest,
                    label: '',
                };
            }

            return opt;
        });

        const optionsUpdated = JSON.stringify(prepareOptionsForComparison(this.props.options)) !== JSON.stringify(prepareOptionsForComparison(nextProps.options));

        return (
            optionsUpdated || 
            this.props.selectedValue !== nextProps.selectedValue ||
            this.props.disabled !== nextProps.disabled ||
            this.props.menuIsOpen !== nextProps.menuIsOpen ||
            this.state.menuIsOpen !== nextState.menuIsOpen
        );
    }

    componentDidMount() {
        this.getMenuPlacement();
    }

    getMenuPlacement() {
        let menuPlacement = 'auto';
        if (this.props.parent && this.props.parent.current != null) {
            let scrollPosition = this.props.parent.current.scrollTop;
            let parentHeight = this.props.parent.current.clientHeight;
            let ddTopPosition = this.selectRef.current.parentElement.offsetTop;
            if (ddTopPosition - scrollPosition < 0 || ddTopPosition > parentHeight + scrollPosition) {
                return;
            }
            let spaceToBottom = parentHeight - ddTopPosition + scrollPosition;
            let spaceToTop = ddTopPosition - scrollPosition - 24;
            let styles = this.state.styles;
            let menuHeight = 200;
            if (spaceToTop > spaceToBottom) {
                menuPlacement = 'top';
                menuHeight = spaceToTop + 12;
            } else {
                menuHeight = spaceToBottom - 36;
                menuPlacement = 'auto';
            }
            styles = {...styles,
                menuList: (base, state) => {
                    let mh = menuHeight + 'px';
                    return {
                        ...base,
                        maxHeight: mh,
                    }
                }
            };
            this.setState({menu: menuPlacement, styles: styles});
        }
    }

    get areNormalizedOptions() {
        const { options = [] } = this.props;

        if (options.length) {
            return typeof options[0] === 'object';
        }

        return false;
    }

    get options() {
        const { options, withEmptyOption } = this.props;

        const processedOptions = options?.map((option) => {
            if (this.areNormalizedOptions) {
                return option;
            }

            return {
                value: option, 
                label: option
            };
        });

        if (withEmptyOption) {
            processedOptions.unshift({ value: '', label: '' });
        }

        return processedOptions;
    }

    get menuIsOpen() {
        return this.props.menuIsOpen !== undefined ? this.props.menuIsOpen : this.state.menuIsOpen;
    }

    handleMenuBlur = (...props) => {
        if (!this.state.menuIsOpen) {
            return 
        }

        this.setState({
            ...this.state,
            menuIsOpen: false
        });
        
        if (this.props.onBlur) {
            this.props.onBlur(...props);
        }
    }

    handleMenuOpen = (...props) => {
        if (this.state.menuIsOpen) {
            return 
        }

        this.setState({
            ...this.state,
            menuIsOpen: true
        });

        if (this.props.onMenuOpen) {
            this.props.onMenuOpen(...props);
        }
    }

    handleMenuClose = (...props) => {
        if (!this.state.menuIsOpen) {
            return 
        }

        this.setState({
            ...this.state,
            menuIsOpen: false
        });

        if (this.props.onMenuClose) {
            this.props.onMenuClose(...props);
        }
    }

    render() {
        const { id, searchable, disabled, tabIndex, selectedValue, className, defaultValue, fullWidth, placeholder, isClearable, menuListComponent, controlComponent, menuIsOpen, onMenuOpen, onMenuClose, onBlur, menuPortalTarget, ...selectProps } = this.props;
        const noValue = selectedValue === undefined || selectedValue === null || selectedValue === '';

        const selectedOption = noValue
            ? null
            : {
                value: selectedValue,
                label: this.areNormalizedOptions  
                    ? this.options?.find((x) => x.value === selectedValue)?.label
                    : selectedValue
            };

        const { styles } = this.state;

        const menuList = menuListComponent || MenuList;

        return (
            <div 
                className="Dropdown" 
                ref={this.selectRef} 
                style={{ opacity: disabled ? .65 : undefined, pointerEvents: disabled ? 'none' : undefined, width: fullWidth ? '100%' : undefined }}
            >
                {this.menuIsOpen && <DropdownBackdrop isCreatePortal={!Boolean(menuPortalTarget)} />}
                <Select
                    {...selectProps}
                    isClearable={isClearable}
                    defaultValue={defaultValue}
                    filterOption={createFilter({ ignoreAccents: false })}
                    styles={{
                        ...styles,
                        menuPortal: menuPortalStyles
                    }}
                    id={id}
                    placeholder={placeholder}
                    title=""
                    menuPortalTarget={menuPortalTarget || document.body}
                    isSearchable={searchable}
                    onChange={this.handleOnChange}
                    value={selectedOption}
                    options={this.options}
                    tabIndex={tabIndex}
                    menuPosition="fixed"
                    className={cx('react-select-container', className)}
                    classNamePrefix="react-select"
                    menuShouldScrollIntoView={false}
                    menuPlacement={this.state.menu}
                    components={{
                        DropdownIndicator,
                        MenuList: menuList,
                        ...(controlComponent ? { Control: controlComponent } : {})
                    }}
                    menuIsOpen={menuIsOpen}
                    onMenuOpen={this.handleMenuOpen}
                    onMenuClose={this.handleMenuClose}
                    onBlur={this.handleMenuBlur}
                />
            </div>
        );
    }
}

Dropdown.propTypes = {
    id: PropTypes.string.isRequired,
    selectedValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    searchable: PropTypes.bool.isRequired,
    options: PropTypes.array,
    onValueChanged: PropTypes.func,
    dropupAuto: PropTypes.bool,
    isDropup: PropTypes.bool,
    optionsHeight: PropTypes.number,
    disabled: PropTypes.bool,
    tabIndex: PropTypes.number,
    withEmptyOption: PropTypes.bool,
};
